import React from "react";
import type { Project } from "../../../../../../api/useProjects";
import Section from "@ui/Section";
import * as io from "io-ts";
import t from "../../../../../../i18n";
import { useTranslations } from "../../../../../../api/useTranslations";
import { NONE_LABEL, buildOptions, buildOptionsFrom } from "@ui/Filter";
import { nullable } from "diagnostics-lib/io";
import BlankStateV2 from "@ui/BlankStateV2";
import { DataProcessing } from "@ui/Illustration/react";
import Table, { columns, getLiveEventTableColumnsLabel, type Column } from "./LiveEventTable";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import {
  DenormalizedEventStatus,
  getEventStatusText,
  getEventStatusWeight,
} from "../../../utils/faultEventStatus";
import { Reparability } from "../../../../../../api/Reparability";
import Modal from "@ui/Modal";
import { useModals } from "@ui/lib/hooks/useModal";
import ModalFormExportEvents from "../../../components/TransactionalModals/ModalFormExportEvents";
import { AddCircleLine, DownloadLine, GitMergeLine } from "@ui/IconV2/react";
import ButtonIconV2 from "@ui/ButtonIconV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import TooltipV2 from "@ui/TooltipV2";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { css } from "@linaria/core";
import PriorityFill from "@ui/IconV2/react/PriorityFill";
import Text from "@ui/Text";
import { spacings } from "@ui/Theme";
import classNames from "classnames";
import AverageLossesWidget from "../../../../Widget/AverageLossesWidget";
import {
  aggregationIntervalToDateValue,
  aggregationPeriodFromPresets,
  aggregationPeriodLabel,
  type AggregationPeriod,
} from "../../../../../../domain/aggregation/period";
import { useSettings } from "../../../../../../api/useSettings";
import EventPriorityWidget from "../../../../Widget/EventPriorityWidget";
import useSparkline from "../../../hooks/charts/sparkline/useSparkline";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";
import LossesRepartitionWidget from "../../../../Widget/LossesRepartitionWidget";
import { useOutletContext } from "react-router";
import type { EventsOutletContext } from "..";
import Button from "@ui/Button";
import { EventType, type Event, type EventDiagnosed } from "../../../../../../api/useEvents";
import {
  getEventIdentifiedAction,
  getEventInProgress,
  getEventPriority,
  getEventSystems,
} from "../../../../../../domain/event";
import { isDiagnosedEvent } from "../../../../../../domain/event/grouping";
import { getEventpriorityOrder, getEventPriorityText } from "../../../utils/event";
import { TableHeader, TableHeaderSpacer } from "@ui/Table/Header";
import {
  TableDisplayMenu,
  TableColumnsMenu,
  TableColumnsOption,
  TableGroupByMenu,
  TableGroupByOption,
  useTableHiddenColumns,
  useTableGroupingResults,
} from "@ui/Table/Display";
import {
  TableFilterReset,
  TableFilterSelect,
  TableFilter,
  TableFilterList,
  useTableFilterResults,
} from "@ui/Table/Filter";
import { TableSearch, useTableSearchResults } from "@ui/Table/Search";
import { useActivePowerPerformanceIndicator } from "../../../../../../api/usePerformanceIndicators";
import { GroupItem, GroupList, GroupToggle } from "@ui/GroupList";
import { BadgeEventStatus } from "../BadgeEventStatus";
import { baseAggregationPeriodPresets } from "../../../../../../domain/aggregation/preset";
import { useAggregationIntervalFromPeriodState } from "../../../../Widget/useAggregationIntervalFromPeriodState";
import useGroups from "../../../../../../api/useGroups";
import type { FilterOption } from "@ui/Filter/FilterSelect";
import ModalFormGroupEvents from "../../../components/TransactionalModals/ModalFormGroupEvents";
import ModalFormUngroupEvent from "../../../components/TransactionalModals/ModalFormUngroupEvent";
import { uniq } from "diagnostics-lib/array";

const EventTableFilterValues = io.strict({
  status: io.array(DenormalizedEventStatus),
  system: io.array(nullable(io.string)),
  reparability: io.array(nullable(Reparability)),
  priority: io.array(nullable(PriorityFaultEvent)),
  active: io.array(io.union([io.literal("true"), io.literal("false")])),
  type: io.array(EventType),
});

type EventTableFilterValues = io.TypeOf<typeof EventTableFilterValues>;
const DEFAULT_EVENT_FILTER_VALUES: EventTableFilterValues = {
  status: [],
  system: [],
  reparability: [],
  priority: [],
  active: ["true"],
  type: [],
};

const GROUP_EVENTS_MODAL = "group-events" as const;
const UNGROUP_EVENT_MODAL = "ungroup-event" as const;
const MIN_GROUPED_EVENTS = 2;

interface P {
  project: Project;
  events: Event[];
  openCreateDrawer: () => void;
}

export const LiveEventPage = () => {
  const { events, project, source, openCreateDrawer } = useOutletContext<EventsOutletContext>();
  if (!project) throw new Error("no project");
  if (!source) throw new Error("no source");

  const inProgressEvents = React.useMemo(
    () => events.filter(event => getEventInProgress(event)),
    [events],
  );

  return (
    <LiveEventView
      project={project}
      events={inProgressEvents}
      openCreateDrawer={openCreateDrawer}
    />
  );
};

const LiveEventView = ({ project, events, openCreateDrawer }: P) => {
  const translations = useTranslations();
  const settings = useSettings();
  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(project.id);
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const { modal, openModal, closeModal } = useModals<
    "export-events" | typeof GROUP_EVENTS_MODAL | typeof UNGROUP_EVENT_MODAL
  >();
  const settingsAggregationPeriod = settings.AGGREGATION_PERIOD as AggregationPeriod;
  const groupingEnabled = settings.FAULT_EVENTS_GROUPING_ENABLED && canUserWriteProject;
  const [selectedFaultEvents, setSelectedFaultEvents] = React.useState<EventDiagnosed[]>([]);
  const [eventToUngroup, setEventToUngroup] = React.useState<EventDiagnosed | null>(null);

  const [aggregationInterval] = useAggregationIntervalFromPeriodState({
    aggregationPeriod: settingsAggregationPeriod,
    projectTimezone: project.timezone,
    projectId: project.id,
    shared: true,
    from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
  });
  const groups = useGroups(project.id);
  const presets = baseAggregationPeriodPresets({
    timezone: project.timezone,
    groups,
    limit: {
      from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
      to: new Date(),
    },
  });
  const aggregationPeriod = aggregationPeriodFromPresets({
    dateValues: aggregationIntervalToDateValue(aggregationInterval, project.timezone),
    presets,
  });

  const activePowerPerformanceIndicator = useActivePowerPerformanceIndicator(project.id);

  const scope = `live-event-${project.id}`;

  const [searchedEvents, isSearchPending] = useTableSearchResults({
    items: events,
    scope,
    index: event => event.name,
  });

  const filterPredicates = React.useMemo<
    Record<
      Extract<Column, "status" | "system" | "reparability" | "priority" | "type" | "active">,
      (filterValues: (string | null)[], item: Event) => boolean
    >
  >(
    () => ({
      status: (filterValues, item) => filterValues.some(v => v === item.metadata.status),
      system: (filterValues, item) => {
        const systemIds = getEventSystems(item);
        return filterValues.some(value =>
          value === null ? systemIds.length === 0 : systemIds.includes(value),
        );
      },
      reparability: (filterValues, item) => filterValues.some(v => v === item.reparability),
      priority: (filterValues, item) => filterValues.some(v => v === getEventPriority(item)),
      type: (filterValues, item) =>
        settings.MANUAL_EVENTS_DEV_MODE_ENABLED ? filterValues.some(v => v === item.type) : true,
      active: (filterValues, item) => filterValues.some(v => v === item.metadata.active.toString()),
    }),
    [settings.MANUAL_EVENTS_DEV_MODE_ENABLED],
  );

  const filteredEvents = useTableFilterResults<
    Event,
    Extract<Column, "status" | "system" | "reparability" | "priority" | "type" | "active">
  >({
    scope,
    items: searchedEvents,
    predicates: filterPredicates,
  });
  const stableFilteredEvents = useDistinctShape(filteredEvents);

  React.useEffect(() => {
    if (!groupingEnabled) {
      setSelectedFaultEvents([]);
      return;
    }

    const selectableEventIds = new Set(
      stableFilteredEvents.filter(isDiagnosedEvent).map(event => event.id),
    );
    setSelectedFaultEvents(previous => {
      const next = previous.filter(event => selectableEventIds.has(event.id));
      if (next.length === previous.length) return previous;
      return next;
    });
  }, [stableFilteredEvents, groupingEnabled]);

  const updateSelectedFaultEvents = React.useCallback(
    (eventsInTableGroup: Event[], selectedEventsInTableGroup: Event[]) => {
      const tableGroupEventIds = new Set(
        eventsInTableGroup.filter(isDiagnosedEvent).map(event => event.id),
      );

      const selectedtableGroupEventIds = new Set(
        selectedEventsInTableGroup.filter(isDiagnosedEvent).map(event => event.id),
      );

      setSelectedFaultEvents(previous => {
        const selectedEventIds = new Set(previous.map(event => event.id));
        for (const eventId of tableGroupEventIds) selectedEventIds.delete(eventId);
        for (const eventId of selectedtableGroupEventIds) selectedEventIds.add(eventId);

        const next = stableFilteredEvents
          .filter(isDiagnosedEvent)
          .filter(event => selectedEventIds.has(event.id));

        if (
          next.length === previous.length &&
          next.every((event, index) => event.id === previous[index]?.id)
        ) {
          return previous;
        }
        return next;
      });
    },
    [stableFilteredEvents],
  );

  const { groupedItems: groupedEvents, groupedBy } = useTableGroupingResults<
    Event,
    Extract<Column, "priority" | "status" | "reparability" | "system">
  >({
    items: stableFilteredEvents,
    groupByDefault: "priority",
    groupBy: {
      priority: item => getEventPriority(item),
      status: item => item.metadata.status,
      reparability: item => item.reparability,
      system: item => {
        const key = uniq(getEventSystems(item))
          .sort((a, b) => a.localeCompare(b))
          .join("-");
        return key === "" ? null : key;
      },
    },
    compareBy: {
      priority: (a, b) => {
        if (PriorityFaultEvent.is(a) && PriorityFaultEvent.is(b)) {
          return getEventpriorityOrder(a) - getEventpriorityOrder(b);
        }
        return 0;
      },
      system: (a, b) => {
        if (typeof a === "string" && typeof b === "string") {
          return a.localeCompare(b);
        }
        return 0;
      },
      status: (a, b) => {
        if (DenormalizedEventStatus.is(a) && DenormalizedEventStatus.is(b)) {
          return getEventStatusWeight(a) - getEventStatusWeight(b);
        }
        return 0;
      },
      reparability: (a, b) => {
        if (Reparability.is(a) && Reparability.is(b)) {
          return a.localeCompare(b);
        }
        return 0;
      },
    },
    scope,
  });
  const stableGroupedEvents = useDistinctShape(groupedEvents);

  const { hiddenColumns, autoHiddenColumns } = useTableHiddenColumns<
    Event,
    Extract<Column, "priority" | "reparability" | "action" | "system" | "type" | "active">
  >({
    items: stableFilteredEvents,
    predicates: {
      priority: () => groupedBy === "priority",
      reparability: item => item.reparability === null,
      action: item => getEventIdentifiedAction(item) === null,
      system: item => getEventSystems(item).length === 0,
      type: () => true,
      active: () => true,
    },
    scope,
  });

  const { sparklineStatisticByEventId, sparklineOptionsByEventId } = useSparkline({
    project,
    performanceIndicator: activePowerPerformanceIndicator,
    aggregationPeriod: aggregationInterval,
    activeAnalyses,
    events: stableFilteredEvents,
  });

  if (events.length === 0)
    return (
      <BlankStateV2
        title={t("project.fault_events.live.blank_state.title")}
        subtitle={t("project.fault_events.live.blank_state.subtitle")}
        picto={<DataProcessing size="l" />}
      />
    );

  return (
    <>
      <Section className={section} id="events-to-process" title="">
        <div className={classNames(widgets)}>
          <EventPriorityWidget
            className={priorityWidget}
            loading={isStreaming}
            projectId={project.id}
            events={events}
          />
          <AverageLossesWidget
            projectTimezone={project.timezone}
            projectId={project.id}
            className={averageLossWidget}
            hidePeriod={false}
            hideLink={false}
            sharedAggregationPeriod
          />
          {project.showPartLoadSensitiveKpis && (
            <>
              <LossesRepartitionWidget
                className={lossesRepartionWidget}
                projectId={project.id}
                projectTimezone={project.timezone}
                sharedAggregationPeriod
              />
            </>
          )}
        </div>

        <TableHeader>
          <TableSearch scope={scope} />
          <TableFilter scope={scope} defaultValues={DEFAULT_EVENT_FILTER_VALUES}>
            <TableFilterSelect<Column>
              filter="status"
              title={t("project.fault_events.table.live.metadata.status")}
              options={buildOptionsFrom(events, {
                value: item => item.metadata.status,
                label: item => getEventStatusText(item.metadata.status),
              })}
            />
            <TableFilterSelect<Column>
              filtered
              filter="system"
              title={t("project.fault_events.table.live.metadata.system")}
              options={buildOptions(
                events.flatMap<FilterOption<string | null>>(event => {
                  const systemIds = getEventSystems(event);

                  if (systemIds.length === 0) {
                    return [
                      {
                        value: null,
                        label: t("project.dashboard.editor.gui.no_system"),
                      },
                    ];
                  }

                  return systemIds.map(systemId => ({
                    value: systemId,
                    label: systemId,
                  }));
                }),
              )}
            />
            <TableFilterSelect<Column>
              filtered
              filter="reparability"
              title={t("project.fault_events.table.live.metadata.reparability")}
              options={buildOptionsFrom(events, {
                value: item => item.reparability,
                label: item =>
                  item.reparability
                    ? t("reparability", item.reparability, translations)
                    : NONE_LABEL,
              })}
            />
            <TableFilterSelect<Column>
              filter="priority"
              title={t("project.events.drawer.create_manual_event.select.priority")}
              options={buildOptionsFrom(events, {
                value: item => getEventPriority(item),
                label: item => getEventPriorityText(getEventPriority(item)),
              })}
            />
            <TableFilterSelect<Column>
              filter="type"
              title="Type"
              options={buildOptionsFrom(events, {
                value: item => item.type,
                label: item =>
                  item.type === "MANUAL"
                    ? t("project.event.type.manual")
                    : t("project.event.type.diagnosed"),
              })}
            />
            <TableFilterSelect<Column>
              width={300}
              filter="active"
              title={t("project.event.active.state")}
              options={buildOptionsFrom(events, {
                value: item => item.metadata.active.toString(),
                description: item =>
                  item.metadata.active
                    ? ""
                    : t("project.fault_events.table.live.switch.cold_events.tooltip.text"),
                label: item =>
                  item.metadata.active ? t("project.event.active") : t("project.event.inactive"),
              })}
            />
            <TableFilterReset defaultValues={DEFAULT_EVENT_FILTER_VALUES} />
          </TableFilter>

          <TableHeaderSpacer />

          <TableDisplayMenu>
            <TableGroupByMenu<Column | "null">
              scope={scope}
              groupByDefault="priority"
              label={{
                priority: t("project.plant_state.block.priority_event.table.priority"),
                system: t("project.fault_events.table.live.metadata.system"),
                status: t("project.fault_events.table.live.metadata.status"),
                reparability: t("project.fault_events.table.live.metadata.reparability"),
                null: t("common.none"),
              }}
            >
              <TableGroupByOption<Column> value={null}>{t("common.none")}</TableGroupByOption>
              <TableGroupByOption<Column> value="priority">
                {t("project.plant_state.block.priority_event.table.priority")}
              </TableGroupByOption>
              <TableGroupByOption<Column> value="reparability">
                {t("project.fault_events.table.live.metadata.reparability")}
              </TableGroupByOption>
              <TableGroupByOption<Column> value="status">
                {t("project.fault_events.table.live.metadata.status")}
              </TableGroupByOption>
              <TableGroupByOption<Column> value="system">
                {t("project.fault_events.table.live.metadata.system")}
              </TableGroupByOption>
            </TableGroupByMenu>

            <TableColumnsMenu scope={scope}>
              {columns
                .filter(c => c !== "type" && c !== "active" && c !== "lastComment")
                .map(column => (
                  <TableColumnsOption<Column>
                    disabled={column === "name" || autoHiddenColumns.includes(column)}
                    key={column}
                    value={column}
                    hidden={hiddenColumns.includes(column)}
                  >
                    {getLiveEventTableColumnsLabel(
                      column,
                      translations,
                      aggregationPeriodLabel(settingsAggregationPeriod),
                    )}
                  </TableColumnsOption>
                ))}
            </TableColumnsMenu>
          </TableDisplayMenu>

          {groupingEnabled && (
            <Button
              variant="secondary"
              leftIcon={<GitMergeLine />}
              onClick={() => openModal(GROUP_EVENTS_MODAL)}
              disabled={selectedFaultEvents.length < MIN_GROUPED_EVENTS}
              slim
            >
              {t("project.fault_events.grouping.button", selectedFaultEvents.length)}
            </Button>
          )}

          {settings.MANUAL_EVENTS_DEV_MODE_ENABLED && canUserWriteProject && (
            <Button
              variant="secondary"
              leftIcon={<AddCircleLine />}
              onClick={openCreateDrawer}
              slim
            >
              {t("project.fault_events.live.button.create_manual_event")}
            </Button>
          )}

          <TooltipV2
            placement="top"
            overlay={<OverlayWrapper text={t("project.fault_events.button.export")} />}
          >
            <ButtonIconV2 onClick={() => openModal("export-events")}>
              <DownloadLine size="m" />
            </ButtonIconV2>
          </TooltipV2>

          <TableFilterList />
        </TableHeader>

        <GroupList
          scope={scope}
          groupedBy={groupedBy}
          groupedItems={stableGroupedEvents}
          updateGroupedItems={updateSelectedFaultEvents}
        >
          {(group, items, updateSelectedItems) => (
            <GroupItem group={group}>
              <GroupToggle>
                {groupedBy === "priority" && PriorityFaultEvent.is(group) ? (
                  <Text type="body1" className={eventPriorityGroup}>
                    <PriorityFill priority={group} />
                    {getEventPriorityText(group)}
                  </Text>
                ) : groupedBy === "system" && typeof group === "string" ? (
                  <Text type="body1">{group}</Text>
                ) : groupedBy === "status" && DenormalizedEventStatus.is(group) ? (
                  <BadgeEventStatus status={group} />
                ) : groupedBy === "reparability" && Reparability.is(group) ? (
                  <Text type="body1">{t("reparability", group, translations)}</Text>
                ) : null}
              </GroupToggle>
              <Table
                aggregationPeriod={aggregationPeriod ?? settingsAggregationPeriod}
                project={project}
                loading={isSearchPending}
                performanceIndicator={activePowerPerformanceIndicator}
                events={items}
                sparklineStatisticByFaultEventId={sparklineStatisticByEventId}
                sparklineOptionsByFaultEventId={sparklineOptionsByEventId}
                isHiddenColumn={column => hiddenColumns.some(c => c === column)}
                groupingSelectionEnabled={groupingEnabled}
                allEvents={stableFilteredEvents}
                selectedEvents={selectedFaultEvents}
                onRowsSelection={updateSelectedItems}
                onUngroupEvent={
                  groupingEnabled
                    ? event => {
                        setEventToUngroup(event);
                        openModal(UNGROUP_EVENT_MODAL);
                      }
                    : undefined
                }
              />
            </GroupItem>
          )}
        </GroupList>
      </Section>

      <Modal
        title={t("project.fault_events.modal.title.export")}
        isOpen={modal === "export-events"}
        onRequestClose={closeModal}
      >
        <ModalFormExportEvents
          project={project}
          onRequestClose={closeModal}
          aggregationInterval={aggregationInterval}
        />
      </Modal>

      <Modal
        title={t("project.fault_events.grouping.modal.title")}
        isOpen={modal === GROUP_EVENTS_MODAL}
        onRequestClose={closeModal}
        size="l"
      >
        <ModalFormGroupEvents
          project={project}
          events={selectedFaultEvents}
          onRequestClose={closeModal}
          onSubmitSuccess={() => setSelectedFaultEvents([])}
        />
      </Modal>
      <Modal
        title={t("project.fault_event.modal.ungroup_event.title")}
        isOpen={modal === UNGROUP_EVENT_MODAL && eventToUngroup !== null}
        onRequestClose={closeModal}
      >
        {eventToUngroup !== null && (
          <ModalFormUngroupEvent
            project={project}
            event={eventToUngroup}
            onRequestClose={closeModal}
          />
        )}
      </Modal>
    </>
  );
};

export default React.memo(LiveEventView);

//#region CSS
const section = css`
  row-gap: 0;
`;

const widgets = css`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.m};
  margin-bottom: ${spacings.l};
  align-items: stretch;

  & > * {
    min-height: 236px;
    height: auto;
    flex-basis: 0%;
    flex-shrink: 1;
    flex-grow: 1;
  }
`;
const priorityWidget = css`
  min-width: 280px;
  width: 280px;
`;
const averageLossWidget = css`
  max-width: 800px;
`;
const lossesRepartionWidget = css`
  max-width: 800px;
`;
const eventPriorityGroup = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;
//#endregion CSS
